import * as THREE from 'three';

/**
 * GPU-side timing using EXT_disjoint_timer_query_webgl2. Wraps a render pass
 * with begin/end (or measure) and reads the actual GPU time a few frames
 * later — that's the only way to ask "is the CPU idle because the GPU is the
 * bottleneck, and if so, which pass is eating the budget?"
 *
 * Limitations:
 *  - Only one query can be active at a time on TIME_ELAPSED_EXT, so begin/end
 *    must be sequential, not nested.
 *  - Results lag 1–3 frames (the CPU can't sync with the GPU without
 *    stalling). Fine for averaged display.
 *  - The extension may be disabled in some browser contexts (notably parts
 *    of incognito); `available()` reports whether it's working.
 */
export interface GpuStats {
  mean: number;
  p95: number;
  max: number;
  count: number;
}

/**
 * Common interface for GPU timing implementations. The WebGL2 implementation
 * (this file) uses EXT_disjoint_timer_query_webgl2 for per-pass timing. The
 * WebGPU implementation (./WebGPUGpuTimer.ts) uses the renderer's built-in
 * timestamp-query support for whole-frame timing. Both expose the same
 * surface so consumers (PerfHud, PerfDiagPanel) don't need to know which
 * backend they're talking to.
 */
export interface PerfGpuTimer {
  available(): boolean;
  begin(name: string): void;
  end(): void;
  measure(name: string, fn: () => void): void;
  /** Optional named compute-group timing for WebGPU `renderer.compute()` calls. */
  measureCompute?(name: string, fn: () => void): void;
  /** Drain pending render-pass timestamp queries. Call once per frame. */
  tick(): void;
  /**
   * Drain pending compute-pass timestamp queries. WebGPU compute kernels
   * (TSL `.compute()` dispatches) are timed in a separate pool from render
   * passes; on WebGL2 this is a no-op. Call once per frame after any
   * `renderer.computeAsync(...)` calls.
   */
  tickCompute(): void;
  stats(): Record<string, GpuStats>;
  totalMean(): number;
  reset(): void;
}

interface TimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

interface InflightQuery {
  name: string;
  query: WebGLQuery;
}

const SAMPLE_LIMIT = 120;

export class GpuTimer {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: TimerExt | null;
  private readonly pool: WebGLQuery[] = [];
  private readonly inflight: InflightQuery[] = [];
  private readonly samples = new Map<string, number[]>();
  private active: InflightQuery | null = null;

  constructor(renderer: THREE.WebGLRenderer) {
    const gl = renderer.getContext();
    // Only WebGL2 supports EXT_disjoint_timer_query_webgl2 in browsers today.
    const isGL2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    this.gl = gl as WebGL2RenderingContext;
    this.ext = isGL2
      ? ((gl as WebGL2RenderingContext).getExtension('EXT_disjoint_timer_query_webgl2') as TimerExt | null)
      : null;
  }

  available(): boolean {
    return this.ext !== null;
  }

  begin(name: string): void {
    if (!this.ext || this.active) return;
    const query = this.pool.pop() ?? this.gl.createQuery();
    if (!query) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
    this.active = { name, query };
  }

  end(): void {
    if (!this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.inflight.push(this.active);
    this.active = null;
  }

  measure(name: string, fn: () => void): void {
    this.begin(name);
    try {
      fn();
    } finally {
      this.end();
    }
  }

  /** WebGL2 has no separate compute pool — see PerfGpuTimer.tickCompute. */
  tickCompute(): void { /* no-op on WebGL */ }

  /** Drain completed queries. Call once per frame, after all measure() calls. */
  tick(): void {
    if (!this.ext) return;
    const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    for (let i = this.inflight.length - 1; i >= 0; i--) {
      const item = this.inflight[i];
      let resolved = false;
      if (disjoint) {
        this.pool.push(item.query);
        resolved = true;
      } else {
        const available = this.gl.getQueryParameter(item.query, this.gl.QUERY_RESULT_AVAILABLE) as boolean;
        if (available) {
          const elapsedNs = this.gl.getQueryParameter(item.query, this.gl.QUERY_RESULT) as number;
          const ms = elapsedNs / 1e6;
          let arr = this.samples.get(item.name);
          if (!arr) {
            arr = [];
            this.samples.set(item.name, arr);
          }
          arr.push(ms);
          if (arr.length > SAMPLE_LIMIT) arr.shift();
          this.pool.push(item.query);
          resolved = true;
        }
      }
      if (resolved) {
        const last = this.inflight.length - 1;
        if (i !== last) this.inflight[i] = this.inflight[last];
        this.inflight.pop();
      }
    }
  }

  stats(): Record<string, GpuStats> {
    const out: Record<string, GpuStats> = {};
    for (const [name, samples] of this.samples) {
      if (samples.length === 0) continue;
      const sorted = [...samples].sort((a, b) => a - b);
      const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
      const p95Idx = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
      out[name] = { mean, p95: sorted[p95Idx], max: sorted[sorted.length - 1], count: samples.length };
    }
    return out;
  }

  /** Mean of the per-pass means — a rough "GPU total" line for at-a-glance display. */
  totalMean(): number {
    let total = 0;
    for (const samples of this.samples.values()) {
      if (samples.length === 0) continue;
      total += samples.reduce((a, b) => a + b, 0) / samples.length;
    }
    return total;
  }

  reset(): void {
    this.samples.clear();
    for (const item of this.inflight.splice(0)) {
      this.gl.deleteQuery(item.query);
    }
    this.active = null;
  }
}
